import { z } from 'zod';

/**
 * Configuración leída del entorno.
 *
 * Se valida al arrancar: si falta una variable, la aplicación falla de
 * inmediato con un mensaje claro en vez de romperse más tarde con un error
 * incomprensible en medio de una pantalla.
 *
 * Ninguna de estas claves se escribe en el código fuente. Ver `.env.example`.
 */
const esquema = z.object({
  VITE_SUPABASE_URL: z.string().url('VITE_SUPABASE_URL debe ser una URL válida'),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, 'Falta VITE_SUPABASE_ANON_KEY'),

  /**
   * Debe coincidir con el `backend_tag` del proyecto Supabase. Si no coincide,
   * la aplicación bloquea el acceso para evitar operar contra un backend
   * cuyo contrato de datos no es el esperado.
   */
  VITE_BACKEND_TAG: z.string().min(1, 'Falta VITE_BACKEND_TAG'),

  /**
   * Claves de terceros. Quedan expuestas en el paquete del navegador, igual
   * que en cualquier aplicación de una sola página: hay que restringirlas por
   * dominio en el panel del proveedor. Sustituirlas por un proxy en una Edge
   * Function es trabajo pendiente documentado en el plan de migración.
   */
  VITE_MAPTILER_KEY: z.string().min(1, 'Falta VITE_MAPTILER_KEY'),
  VITE_ORS_API_KEY: z.string().optional().default(''),

  /**
   * Dónde publica el servidor de vídeo los caminos de las cámaras. El agente
   * del servidor crea cada camino con ese mismo nombre, y la consola compone
   * `<base>/<camino>/index.m3u8` para reproducirlo.
   */
  VITE_STREAM_BASE: z.string().default(''),

  VITE_APP_NAME: z.string().default('VenIoT'),
  VITE_CLIENT_NAME: z.string().default('Omsaica'),
});

const resultado = esquema.safeParse(import.meta.env);

if (!resultado.success) {
  const detalles = resultado.error.issues.map((i) => `  · ${i.message}`).join('\n');
  throw new Error(
    `Configuración de entorno inválida.\n${detalles}\n\n` +
      'Copia .env.example a .env y completa los valores.',
  );
}

const vars = resultado.data;

export const env = {
  supabase: {
    url: vars.VITE_SUPABASE_URL,
    anonKey: vars.VITE_SUPABASE_ANON_KEY,
  },
  backendTag: vars.VITE_BACKEND_TAG,
  mapa: {
    maptilerKey: vars.VITE_MAPTILER_KEY,
    orsApiKey: vars.VITE_ORS_API_KEY,
  },
  // Sin barra final, la añade quien compone el enlace.
  baseDeVideo: vars.VITE_STREAM_BASE.replace(/\/+$/, ''),
  marca: {
    nombreApp: vars.VITE_APP_NAME,
    nombreCliente: vars.VITE_CLIENT_NAME,
  },
  esDesarrollo: import.meta.env.DEV,
} as const;

/** Prefijo único para todo lo que la aplicación guarda en el navegador. */
export const STORAGE_PREFIX = 'veniot:';
